// src/Layout.js
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Search,
  Map,
  Shield,
  TrendingUp,
  Target,
  MessageSquare, Inbox, NotebookPen, Network, FlaskConical, Scale,
  Menu,
  LogOut,
  Trash2,
  Bell,
  ChevronsUpDown,
  Monitor,
  Sun,
  Moon,
  Plug,
  Bot,
  Video,
  Compass,
} from "lucide-react";
import { useAuth } from "./contexts/AuthContext";
import { useProductBrain } from './contexts/ProductBrainContext';
import { SoundMenuItem } from "./components/ui/ActionFeedback";
import { productContext } from './lib/productContext';
import { useGuide } from "./contexts/GuideContext";
import LoopDock from "./components/guide/LoopDock";
import { clearAllUserData } from "./services/dataManagement";
import { runJob } from "./lib/backgroundJobs";

const NAV_ITEMS = [
  { title: "Command Center", to: "/", icon: LayoutDashboard, group: "Workspace", hue: "indigo" },
  { title: "Autopilot", to: "/autopilot", icon: Bot, group: "Workspace", hue: "teal" },
  { title: "Meeting agent", to: "/meetings", icon: Video, group: "Workspace", hue: "cyan" },
  { title: "Ask SPCC", to: "/ask", icon: MessageSquare, group: "Workspace", hue: "violet" },
  { title: "Ticket writer", to: "/tickets", icon: NotebookPen, group: "Workspace", hue: "indigo" },
  { title: "Inbox", to: "/inbox", icon: Inbox, group: "Workspace", hue: "sky" },
  { title: "Setup & integrations", to: "/onboarding", icon: Plug, group: "Workspace", hue: "teal" },
  { title: "Discovery Hub", to: "/discovery", icon: Search, group: "Discover & decide", hue: "cyan" },
  { title: "Priorities", to: "/priorities", icon: Scale, group: "Discover & decide", hue: "amber" },
  { title: "Decisions", to: "/decisions", icon: NotebookPen, group: "Discover & decide", hue: "fuchsia" },
  { title: "Outcome Roadmap", to: "/roadmap", icon: Map, group: "Plan & learn", hue: "emerald" },
  { title: "OKRs", to: "/okrs", icon: Target, group: "Plan & learn", hue: "orange" },
  { title: "Learning", to: "/learning", icon: FlaskConical, group: "Plan & learn", hue: "lime" },
  { title: "Product Brain", to: "/brain", icon: Network, group: "Plan & learn", hue: "pink" },
  { title: "Firefighter", to: "/issues", icon: Shield, group: "Operate", hue: "rose" },
  { title: "Time Audit", to: "/time", icon: TrendingUp, group: "Operate", hue: "sky" },
  { title: "Reminders", to: "/reminders", icon: Bell, group: "Operate", hue: "amber" },
];

const THEMES = [
  { value: "system", label: "System", icon: Monitor },
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
];

// Persists the theme choice; "system" defers to prefers-color-scheme.
function useTheme() {
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem("spcc:theme") || "system"; } catch { return "system"; }
  });
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "system") delete root.dataset.theme;
    else root.dataset.theme = theme;
    try { localStorage.setItem("spcc:theme", theme); } catch { /* storage unavailable */ }
  }, [theme]);
  return [theme, setTheme];
}

function GuideMenuItem({ onDone }) {
  const guide = useGuide();
  if (!guide?.activation) return null;
  const paused = guide.activation.paused;
  return <button type="button" role="menuitem" className="menu-item" onClick={() => { onDone(); guide.setPaused(!paused); }}><Compass size={15} /> {paused ? "Resume guide" : "Pause guide"}</button>;
}

function UserMenu({ user, onLogout, onClearData }) {
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useTheme();
  const ref = useRef(null);
  const name = user?.name || user?.email?.split("@")[0] || "User";

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);

  return (
    <div className="sidebar-footer" ref={ref}>
      {open && (
        <div className="menu" role="menu" aria-label="Account">
          <div className="menu-label">Theme</div>
          {THEMES.map(({ value, label, icon: Icon }) => (
            <button key={value} type="button" role="menuitemradio" aria-checked={theme === value} className="menu-item" onClick={() => setTheme(value)}>
              <Icon size={15} /> {label}
            </button>
          ))}
          <div className="menu-sep" role="separator" />
          <SoundMenuItem />
          <GuideMenuItem onDone={() => setOpen(false)} />
          <div className="menu-sep" role="separator" />
          <button type="button" role="menuitem" className="menu-item" onClick={() => { setOpen(false); onLogout(); }}>
            <LogOut size={15} /> Sign out
          </button>
          <div className="menu-sep" role="separator" />
          <button type="button" role="menuitem" className="menu-item danger" onClick={() => { setOpen(false); onClearData(); }}>
            <Trash2 size={15} /> Clear all data…
          </button>
        </div>
      )}
      <button type="button" className="user-button" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span className="avatar" aria-hidden="true">{name.charAt(0).toUpperCase()}</span>
        <span className="who">
          <strong className="text-ellipsis">{name}</strong>
          <span className="text-ellipsis">{user?.email}</span>
        </span>
        <ChevronsUpDown size={14} />
      </button>
    </div>
  );
}

export default function Layout({ children }) {
  const { pathname } = useLocation();
  const { workspace } = useProductBrain() || {};
  const focused = productContext(workspace).navigation === 'focused';
  const corePaths = ['/', '/ask', '/inbox', '/onboarding', '/discovery', '/priorities', '/decisions', '/learning'];
  const visibleNav = focused ? NAV_ITEMS.filter(item => corePaths.includes(item.to) || item.to === pathname) : NAV_ITEMS;
  const additionalNav = focused ? NAV_ITEMS.filter(item => !visibleNav.includes(item)) : [];
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();

  const isActive = (to) => pathname === to;

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      logout();
    }
  };

  const handleClearAllData = async () => {
    if (window.confirm('This will permanently delete ALL your data (problems, roadmap, issues, time audit, etc.). This action cannot be undone.\n\nAre you sure you want to continue?')) {
      if (window.confirm('FINAL CONFIRMATION: Delete everything and reset to a clean state?')) {
        try {
          await runJob('Clearing all data', () => clearAllUserData());
          alert('All data cleared. The page will refresh.');
          window.location.reload();
        } catch (error) {
          console.error('Error clearing data:', error);
          alert('Error clearing data. Please try again or contact support.');
        }
      }
    }
  };

  // Close on Esc
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setMobileOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Close when route changes
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  // Lock body scroll when open
  useEffect(() => {
    if (mobileOpen) document.body.classList.add("no-scroll");
    else document.body.classList.remove("no-scroll");
  }, [mobileOpen]);

  const activeItem = NAV_ITEMS.find((item) => isActive(item.to));

  return (
    <div className="app-shell" style={{ "--page-hue": `var(--c-${activeItem?.hue || "indigo"})` }}>
      <a href="#main" className="skip-link">Skip to content</a>

      <aside id="sidebar" className={`sidebar ${mobileOpen ? "open" : ""}`} aria-label="Primary">
        <Link to="/" className="sidebar-brand" style={{ textDecoration: "none" }}>
          <img src="/spcc-logo.png" alt="" />
          <div>
            <strong>SPCC</strong>
            <span>Product Command Center</span>
          </div>
        </Link>

        <nav className="sidebar-scroll" aria-label="Main navigation">
          {visibleNav.map((item, index) => {
            const active = isActive(item.to);
            return (
              <React.Fragment key={item.title}>
                {(index === 0 || visibleNav[index - 1].group !== item.group) && <div className="nav-group-label">{item.group}</div>}
                <Link
                  to={item.to}
                  className={`nav-item${active ? " active" : ""}`}
                  style={{ "--nav-hue": `var(--c-${item.hue})` }}
                  aria-current={active ? "page" : undefined}
                  onClick={() => setMobileOpen(false)}
                >
                  <item.icon size={16} strokeWidth={1.75} aria-hidden="true" />
                  <span className="text-ellipsis">{item.title}</span>
                </Link>
              </React.Fragment>
            );
          })}
          {additionalNav.length > 0 && <details className="nav-more"><summary>More workflows</summary>{additionalNav.map(item => <Link key={item.to} to={item.to} className="nav-item" onClick={() => setMobileOpen(false)}><item.icon size={16} aria-hidden="true"/><span>{item.title}</span></Link>)}</details>}
        </nav>

        <UserMenu user={user} onLogout={handleLogout} onClearData={handleClearAllData} />
      </aside>

      <div
        className={`sidebar-overlay ${mobileOpen ? "open" : ""}`}
        onClick={() => setMobileOpen(false)}
        aria-hidden
      />

      <div className="main-area">
        <header className="main-header">
          <button
            type="button"
            onClick={() => setMobileOpen((s) => !s)}
            className="btn btn-ghost btn-icon"
            aria-label="Open navigation"
            aria-expanded={mobileOpen}
            aria-controls="sidebar"
          >
            <Menu size={18} />
          </button>
          <img src="/spcc-logo.png" alt="" />
          <strong>SPCC</strong>
        </header>

        <main id="main" className="main-content" tabIndex={-1}>{children}</main>
      </div>
      <LoopDock />
    </div>
  );
}
